import { useMemo } from "react";
import { View, ViewStyle } from "react-native";
import { GestureDetector } from "react-native-gesture-handler";
import useDevice from "src/hooks/useDevice";
import { useSafeArea } from "src/swsh-native";
import useImageGalleryDimensions from "../../hooks/useImageGalleryDimensions";
import usePhotoDetailHeaderDimensions from "../../hooks/usePhotoDetailHeaderDimensions";
import usePhotoDetailStore from "../../stores/usePhotoDetailStore";
import { PhotoItem } from "../../types";
import MainPhotoItemContent from "./components/MainPhotoItemContent/MainPhotoItemContent";
import MainPhotoItemZoomWrapper from "./components/MainPhotoItemZoomWrapper";
import MainPhotoOverlay from "./components/MainPhotoOverlay";

const MainPhotoItem = ({
	photo,
	width,
	height,
	isInitial,
}: {
	photo: PhotoItem;
	width: number;
	height: number;
	isInitial: boolean;
}) => {
	const { device } = useDevice();
	const isFocused = usePhotoDetailStore(
		(state) => (state.curPhoto && state.curPhoto.photoId === photo.photoId) ?? undefined,
	);
	const isMain: boolean = !!((isInitial && isFocused === undefined) || isFocused);
	const shouldAttemptLoad = usePhotoDetailStore((state) => {
		if (isInitial) return true;
		if (isMain) return true;
		return !state.isWaitingForCurPhotoLoad;
	});
	const { carouselItemDimensions } = useImageGalleryDimensions();
	const safeArea = useSafeArea();

	const paddingTop = usePhotoDetailHeaderDimensions().height;
	const paddingBottom = useMemo(
		() =>
			safeArea.bottom +
			carouselItemDimensions.height +
			carouselItemDimensions.paddingVertical * 2,
		[carouselItemDimensions, safeArea],
	);
	const paddingHorizontal = useMemo(() => (device === "desktop" ? 126 : 0), [device]);

	const { width: boundingWidth, height: boundingHeight } = useMemo(() => {
		return {
			width: width - paddingHorizontal * 2,
			height: height - paddingTop - paddingBottom,
		};
	}, [height, width, paddingBottom, paddingTop, paddingHorizontal]);
	const { width: photoWidth, height: photoHeight } = useMemo(() => {
		if (!photo.width || !photo.height) {
			return { width: boundingWidth, height: boundingHeight };
		}
		// Contain the photo within the innerWidth and innerHeight
		const aspectRatio = photo.width / photo.height;
		if (aspectRatio > boundingWidth / boundingHeight) {
			return {
				width: boundingWidth,
				height: boundingWidth / aspectRatio,
			};
		}
		return {
			width: boundingHeight * aspectRatio,
			height: boundingHeight,
		};
	}, [boundingHeight, boundingWidth, photo.height, photo.width]);
	const createMainContentGesture = usePhotoDetailStore((state) => state.createMainContentGesture);
	const mainContentGesture = useMemo(() => {
		if (!createMainContentGesture) return null;
		return createMainContentGesture();
	}, [createMainContentGesture]);

	const containerStyle = useMemo<ViewStyle>(() => {
		return {
			position: "absolute",
			width,
			height,
			overflow: "hidden",
			justifyContent: "center",
			alignItems: "center",
			paddingTop,
			paddingBottom,
		};
	}, [height, paddingBottom, paddingTop, width]);
	const innerStyle = useMemo<ViewStyle>(() => {
		return {
			width: boundingWidth,
			height: boundingHeight,
			alignItems: "center",
			justifyContent: "center",
		};
	}, [boundingHeight, boundingWidth]);

	return (
		<>
			{mainContentGesture && (
				<GestureDetector gesture={mainContentGesture}>
					<View style={containerStyle}>
						{shouldAttemptLoad && (
							<MainPhotoItemZoomWrapper
								style={{
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<View style={innerStyle} pointerEvents="box-none">
									<MainPhotoItemContent
										photo={photo}
										isInitial={isInitial}
										boundingWidth={boundingWidth}
										boundingHeight={boundingHeight}
										mainContentGesture={mainContentGesture}
									/>
								</View>
							</MainPhotoItemZoomWrapper>
						)}
					</View>
				</GestureDetector>
			)}
			<View style={containerStyle} pointerEvents="box-none">
				<View style={innerStyle} pointerEvents="box-none">
					<MainPhotoOverlay photo={photo} width={photoWidth} height={photoHeight} />
				</View>
			</View>
		</>
	);
};
export default MainPhotoItem;
